import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withTranslation, useTranslation } from 'react-i18next';
import { titleize } from 'underscore.string';
import { uniqBy, without } from 'lodash';
import { Plus } from 'lucide-react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@cargomatic/ui';
import {
  getShipment,
  updateShipmentStopEquipment,
} from 'utils/ShipmentWebAPIUtils';

import AddEquipmentForm from 'components/modules/equipment/AddEquipmentForm';
import LoadingCircle from 'components/ui/LoadingCircle';
import Tooltip from 'components/v2/Tooltip';
import EquipmentActionCreators from 'actions/EquipmentActionCreators';
import EquipmentStore from 'stores/EquipmentStore';
import {
  containerExists,
  findContainer,
  findTrailer,
  getAvailableEquipment,
  getUnmappedAccessorialRequests,
  trailerExists,
} from 'helpers/equipment';
import { shipperHasTag } from 'utils/formatters/shipments';
import { checkEquipmentExists } from 'utils/EquipmentWebAPIUtils';
import { sluggify } from 'utils/localization/utils';
import Alert from '../../../ui/Alert';
import EquipmentTable from '../EquipmentTable';
import EquipmentSearch from '../../orders/equipments/EquipmentSearch';
import OrphanAdjustments from '../load/OrphanAdjustments';
import { isPrimaryStopForShipment } from 'helpers/stops';
import { getAccessorialRequests } from 'utils/AccessorialRequestWebAPIUtils';

const getInitialState = () => {
  return {
    success: false,
    error: null,
    showAddNewEquipmentForm: false,
    availableEquipment: [],
    equipments: [],
    equipmentSearchText: '',
    selectedReceivables: [],
    selectedPayables: [],
    selectedUnmappedAccessorialRequests: [],
    addNewEquipmentError: null,
    unmappedAccessorialRequests: [],
  };
};

function StopEquipmentModalTitle() {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-1">
      <span>{t('STOP_EQUIPMENT')}</span>
      <Tooltip id="select-equipment">{t('STOP_EQUIPMENT_NOTE')}</Tooltip>
    </div>
  );
}

function StopEquipmentModalFooter({
  cancelDisabled,
  saveDisabled,
  onCancel,
  onSave,
}) {
  const { t } = useTranslation();
  return (
    <>
      <Button variant="outline" disabled={cancelDisabled} onClick={onCancel}>
        {t('CANCEL')}
      </Button>
      <Button onClick={onSave} disabled={saveDisabled}>
        {titleize(t('SAVE'))}
      </Button>
    </>
  );
}

class StopEquipmentModal extends Component {
  static propTypes = {
    shipmentId: PropTypes.string.isRequired,
    stopId: PropTypes.string.isRequired,
    show: PropTypes.bool.isRequired,
    onStopEquipmentUpdateSuccess: PropTypes.func,
    onStopEquipmentUpdateFail: PropTypes.func,
    onHide: PropTypes.func.isRequired,
    isCarrier: PropTypes.bool,
    carrierPartner: PropTypes.string,
  };

  static defaultProps = {
    onStopEquipmentUpdateSuccess: () => {},
    onStopEquipmentUpdateFail: () => {},
    isCarrier: true,
  };

  constructor(props) {
    super(props);
    this.state = {
      ...getInitialState(props),
      shipment: {},
      stop: {},
      loaded: false,
    };
  }

  componentDidMount() {
    EquipmentStore.addChangeListener(this.getCreatedEquipment);
    this.fetchShipment();
  }

  componentWillUnmount() {
    EquipmentStore.removeChangeListener(this.getCreatedEquipment);
  }

  fetchShipment = async () => {
    const { shipmentId, stopId } = this.props;

    try {
      const [shipmentResult, shipmentAccessorialResult] = await Promise.all([
        getShipment(shipmentId),
        getAccessorialRequests({
          shipment: shipmentId,
        }),
      ]);
      const shipment = shipmentResult?.entity?.item || {};

      let stops =
        shipment?.stops?.map((stop) => ({
          ...stop,
          isPrimary: !!isPrimaryStopForShipment(stop, shipment),
        })) || [];

      const isPrimaryStopAvailable = stops.some((stop) => stop.isPrimary);

      if (!isPrimaryStopAvailable) {
        stops = stops.map((stop) => {
          return { ...stop, isPrimary: stop.sequence === 1 && !stop.off_site };
        });
      }

      const stop = stops.find((s) => s._id === stopId);

      const equipments = uniqBy(
        (stop?.equipments || []).map((eq) => ({
          ...eq,
          canRemove: true,
        })),
        (eq) => eq._id,
      );

      let selectedReceivables = [];
      let selectedPayables = [];
      let selectedUnmappedAccessorialRequests = [];
      const availableEquipment = getAvailableEquipment(shipment, equipments);
      const unmappedAccessorialRequests = getUnmappedAccessorialRequests(
        shipment,
        shipmentAccessorialResult?.data?.request,
      );
      if (stop?.isPrimary) {
        selectedReceivables = shipment.receivables
          .filter((adj) => !adj?.equipment)
          .map((r) => r._id);
        selectedPayables = shipment.payables
          .filter((adj) => !adj?.equipment)
          .map((p) => p._id);
        selectedUnmappedAccessorialRequests = unmappedAccessorialRequests.map(
          (item) => item._id,
        );
      }

      const hasDrayageTrailerMovesTag = shipperHasTag(
        shipment,
        'drayage_trailer_moves',
        shipment.shipment_type === 'drayage',
      );

      this.setState({
        shipment,
        stop,
        availableEquipment,
        equipments,
        loaded: true,
        saving: false,
        selectedReceivables,
        selectedPayables,
        hasDrayageTrailerMovesTag,
        unmappedAccessorialRequests,
        selectedUnmappedAccessorialRequests,
      });
    } catch (e) {
      console.error(e);
    }
  };

  disabled() {
    const { showAddNewEquipmentForm, saving } = this.state;

    return showAddNewEquipmentForm || saving;
  }

  getCreatedEquipment = () => {
    const equipment = EquipmentStore.getCreatedEquipment();
    this.handleEquipmentSearchSelect(equipment);
    this.setState({ showAddNewEquipmentForm: false });
  };

  // Create equipment using form
  async createEquipment(equipment) {
    try {
      const { entity: equipmentData } = await checkEquipmentExists(equipment);
      if (equipmentData.exists === true) {
        this.setState({ addNewEquipmentError: 'EQUIPMENT_ALREADY_EXIST' });
      }
      if (equipmentData.exists === false) {
        this.setState({ addNewEquipmentError: null }, () =>
          EquipmentActionCreators.addEquipment(equipment),
        );
      }
    } catch (e) {
      if (sluggify(e.entity.message) === 'EQUIPMENT_MISMATCH') {
        this.setState({ addNewEquipmentError: 'MISMATCHED_EQUIPMENT_TYPE' });
      }
    }
  }

  handleToggleOrphanedAdjustment = (type, item, checked) => {
    const typeToStateKey = {
      receivables: 'selectedReceivables',
      payables: 'selectedPayables',
      accessorials: 'selectedUnmappedAccessorialRequests',
    };

    if (!typeToStateKey[type]) return;

    this.setState((prevState) => ({
      [typeToStateKey[type]]: this.handleSelectionChange(
        item,
        checked,
        prevState[typeToStateKey[type]],
      ),
    }));
  };

  handleSelectionChange = (item, checked, list) => {
    if (checked) {
      list.push(item._id);
    } else {
      list = without(list, item._id);
    }
    return list;
  };

  handleAdjustmentChange(current, updates) {
    if (updates && updates.length) {
      const { equipments, hasDrayageTrailerMovesTag } = this.state;
      const container = findContainer(equipments);
      const trailer = findTrailer(equipments);

      const fallbackEquipment = hasDrayageTrailerMovesTag ? trailer : container;

      updates.forEach((update_id) => {
        // Allow for the potential of duplicate _id under a ledger. Brought up due to multi-invoice migration 03/21
        const items = current.filter((item) => item._id === update_id);
        if (items) {
          items.forEach((item) => {
            item.equipment = item.equipment
              ? item.equipment
              : fallbackEquipment;
          });
        } else {
          console.log(`Did not find id ${update_id} in current list`);
        }
      });
    }
    return current;
  }

  handleEquipmentRemove = (equipment) => {
    this.setState((prevState) => {
      const equipments = prevState.equipments.filter(
        (eq) => eq._id !== equipment._id,
      );
      const availableEquipment = uniqBy(
        [...prevState.availableEquipment, { ...equipment, canAdd: true }],
        (eq) => eq._id,
      );
      return {
        equipments,
        availableEquipment,
      };
    });
  };

  // Add user equipment
  handleEquipmentSelect = (equipment) => {
    this.setState((prevState) => {
      const equipments = uniqBy(
        [...prevState.equipments, { ...equipment, canRemove: true }],
        (eq) => eq._id,
      );
      return {
        equipments,
        availableEquipment: prevState.availableEquipment
          .filter((eq) => eq._id !== equipment._id)
          .map((eq) => ({ ...eq, canAdd: true })),
      };
    });
  };

  handleEquipmentSearchSelect = (equipment) => {
    this.setState((prevState) => {
      const equipments = uniqBy(
        [...prevState.equipments, { ...equipment, canRemove: true }],
        (eq) => eq._id,
      );
      return {
        equipments,
      };
    });
  };

  handleSave = () => {
    const { isCarrier, carrierPartner } = this.props;
    const {
      shipment,
      shipment: { receivables, payables },
      stop,
      selectedReceivables,
      selectedPayables,
      selectedUnmappedAccessorialRequests,
      equipments,
      hasDrayageTrailerMovesTag,
    } = this.state;

    const container = findContainer(equipments);
    const trailer = findTrailer(equipments);

    const fallbackEquipment = hasDrayageTrailerMovesTag ? trailer : container;

    const equipmentList = uniqBy(equipments, (eq) => eq._id) || [];

    const changes = {
      equipments: equipmentList,
      accessorials: fallbackEquipment
        ? selectedUnmappedAccessorialRequests.map((acc) => ({
            _id: acc,
            equipment: [fallbackEquipment],
          }))
        : [],
    };

    if (!isCarrier) {
      const adjustedReceivables = this.handleAdjustmentChange(
        receivables,
        selectedReceivables,
      );
      const adjustedPayables = this.handleAdjustmentChange(
        payables,
        selectedPayables,
      );

      changes.receivables = adjustedReceivables;
      changes.payables = adjustedPayables;
    }

    this.setState({ saving: true }, async () => {
      try {
        const {
          entity: { job },
        } = await updateShipmentStopEquipment(
          shipment,
          stop,
          changes,
          isCarrier,
          carrierPartner,
        );

        this.setState(
          (prevState) => ({ ...prevState, ...getInitialState() }),
          () => {
            this.props.onHide();
            // Need to check for duplicates before saving
            this.props.onStopEquipmentUpdateSuccess({ job });
          },
        );
      } catch (e) {
        this.props.onStopEquipmentUpdateFail(e);
      }
    });
  };

  renderEquipmentTable() {
    const { isCarrier, t } = this.props;
    const { equipments, availableEquipment, hasDrayageTrailerMovesTag } =
      this.state;
    if (equipments.length === 0 && availableEquipment.length === 0) return null;

    const showChargesForTrailerMovesTag =
      !hasDrayageTrailerMovesTag ||
      equipments.some(({ _equipmentType }) => _equipmentType === 'trailer');
    return (
      <div className="pt-4">
        <h4 className="my-0 mb-2">{t('CURRENT_EQUIPMENT')}</h4>
        <div className="mb-4">
          <EquipmentTable
            equipments={this.state.equipments}
            availableEquipment={this.state.availableEquipment}
            shipmentId={this.props.shipmentId}
            showAvailability={!isCarrier}
            showActions
            onRemove={this.handleEquipmentRemove}
            onAdd={this.handleEquipmentSelect}
          />
        </div>
        {showChargesForTrailerMovesTag && this.renderOrphanedAdjustments()}
      </div>
    );
  }

  renderOrphanedAdjustments() {
    const { isCarrier } = this.props;

    if (isCarrier) {
      return null;
    }

    const {
      shipment: { receivables, payables },
      showAddNewEquipmentForm,
      selectedPayables,
      selectedReceivables,
      unmappedAccessorialRequests,
      selectedUnmappedAccessorialRequests,
      saving,
    } = this.state;

    if (showAddNewEquipmentForm) {
      return null;
    }

    const receivableOrphans = (receivables || []).filter(
      (receivable) => !receivable.equipment,
    );
    const payableOrphans = (payables || []).filter(
      (payable) => !payable.equipment,
    );

    const { equipments, hasDrayageTrailerMovesTag } = this.state;

    return (
      <div className="mt-4">
        {((hasDrayageTrailerMovesTag && trailerExists(equipments)) ||
          containerExists(equipments)) &&
          !saving && (
            <OrphanAdjustments
              equipments={equipments}
              receivables={receivableOrphans}
              payables={payableOrphans}
              unmappedAccessorialRequests={unmappedAccessorialRequests}
              onSelect={this.handleToggleOrphanedAdjustment}
              selectedPayables={selectedPayables}
              selectedReceivables={selectedReceivables}
              selectedUnmappedAccessorialRequests={
                selectedUnmappedAccessorialRequests
              }
            />
          )}
      </div>
    );
  }

  renderAddEquipmentButton() {
    if (this.state.showAddNewEquipmentForm) {
      return null;
    }
    const { t } = this.props;
    return (
      <div className="pt-4">
        <div className="flex space-x-1 items-center mb-2">
          <h4 className="my-0">{t('ADD_NEW_EQUIPMENT')}</h4>
          <Tooltip id="equipment-not-found">
            {t('ADD_NEW_EQUIPMENT_NOTE')}
          </Tooltip>
        </div>
        <Button
          onClick={() => this.setState({ showAddNewEquipmentForm: true })}
        >
          <Plus className="size-4 mr-1" />
          {t('NEW_EQUIPMENT')}
        </Button>
      </div>
    );
  }

  renderEquipmentSearch() {
    if (this.state.showAddNewEquipmentForm) {
      return null;
    }
    const { t } = this.props;
    return (
      <div className="pb-4">
        <div className="flex items-center mb-2 space-x-1">
          <h4 className="my-0">{t('EQUIPMENT_SEARCH')}</h4>
          <Tooltip className="text-sm" id="equipment-number">
            {t('EQUIPMENT_SEARCH_NOTE')}
          </Tooltip>
        </div>
        <EquipmentSearch
          onEquipmentSelect={(e) => this.handleEquipmentSearchSelect(e)}
          onSearchChange={(equipmentSearchText) =>
            this.setState({ equipmentSearchText })
          }
        />
      </div>
    );
  }

  renderAddEquipmentForm() {
    const { t } = this.props;
    const {
      addNewEquipmentError,
      showAddNewEquipmentForm,
      equipmentSearchText,
    } = this.state;
    if (!showAddNewEquipmentForm) {
      return null;
    }
    return (
      <div className="">
        {addNewEquipmentError && (
          <Alert title={t('ERRORS')} type="error">
            <ul>
              <li key={1}>{t(addNewEquipmentError)}</li>
            </ul>
          </Alert>
        )}
        <AddEquipmentForm
          equipmentId={equipmentSearchText}
          onSave={(equipment) => this.createEquipment(equipment)}
          onCancel={() => this.setState({ showAddNewEquipmentForm: false })}
        />
      </div>
    );
  }
  renderBody() {
    if (!this.state.loaded) {
      return <LoadingCircle size={2} />;
    }
    const { t } = this.props;
    return (
      <div>
        {this.state.error && (
          <Alert type="error" title={t('ERROR')} className="mb-4" />
        )}
        <div className="divide-y divide-solid divide-gray-200">
          {this.renderEquipmentSearch()}
          {this.renderEquipmentTable()}
          {this.renderAddEquipmentButton()}
          {this.renderAddEquipmentForm()}
        </div>
      </div>
    );
  }

  render() {
    const handleOpenChange = (open) => {
      if (!open) {
        this.props.onHide();
      }
    };

    return (
      <Dialog open={this.props.show} onOpenChange={handleOpenChange}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              <StopEquipmentModalTitle />
            </DialogTitle>
          </DialogHeader>
          {this.renderBody()}
          <DialogFooter>
            <StopEquipmentModalFooter
              cancelDisabled={this.state.saving}
              saveDisabled={this.disabled()}
              onCancel={() => this.props.onHide()}
              onSave={this.handleSave}
            />
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }
}

export default withTranslation()(StopEquipmentModal);
